import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import Swal from 'sweetalert2';
import { AuthService } from 'src/app/services/auth/auth.service';
import { DayClosingRun, DayClosingScope, DayGroup, EventDayService } from 'src/app/services/event-day/event-day.service';

type Step = 'view' | 'working' | 'done';

/**
 * Cierre del día (rol 11 y administradores): marca las órdenes de la
 * jornada anterior —o todas las pendientes— con su día operativo y deja
 * constancia con los totales por día y por caja. Después del cierre la caja
 * arranca el día con la lista limpia, y el cliente (rol 12) puede ver las
 * estadísticas día por día. No pide arqueo: eso sigue siendo el cierre de
 * caja de cada zona.
 */
@Component({
    selector: 'app-day-closing',
    templateUrl: './day-closing.component.html',
    styleUrls: ['./day-closing.component.scss'],
})
export class DayClosingComponent implements OnInit {

    @Input() event_key: string = '';
    @Input() event_name: string = '';

    step: Step = 'view';
    loading = true;
    error = '';

    /** Todas las órdenes sin cerrar, agrupadas por día operativo. */
    pending: any[] = [];
    groups: DayGroup[] = [];
    scope: DayClosingScope = 'until_yesterday';
    lastRun: DayClosingRun | null = null;
    result: DayClosingRun | null = null;
    progressText = '';

    readonly today = EventDayService.today();

    constructor(
        private modalCtrl: ModalController,
        private service: EventDayService,
        public auth: AuthService,
    ) { }

    async ngOnInit() {
        await this.load();
    }

    async load() {
        this.loading = true;
        this.error = '';
        try {
            const [pending, runs] = await Promise.all([
                this.service.getPendingOrders(this.event_key),
                this.service.getClosingRuns(this.event_key),
            ]);
            this.pending = pending;
            this.groups = EventDayService.groupByDay(pending);
            await this.service.resolveBoxNames(this.groups);
            this.lastRun = runs.length ? runs[0] : null;
            // Por defecto, lo de ayer hacia atrás; si solo hay de hoy, se ofrece todo.
            this.scope = this.untilYesterdayCount > 0 ? 'until_yesterday' : 'all';
        } catch (e) {
            console.error('No se pudieron leer las órdenes pendientes:', e);
            this.error = 'No se pudieron leer las órdenes del evento. Revisa la conexión e intenta de nuevo.';
        }
        this.loading = false;
    }

    close(data?: any) {
        this.modalCtrl.dismiss(data);
    }

    // ── Alcance ────────────────────────────────────────────────────────────

    setScope(scope: DayClosingScope) {
        this.scope = scope;
    }

    get untilYesterdayCount(): number {
        return EventDayService.selectForClosing(this.pending, 'until_yesterday').length;
    }

    get allCount(): number {
        return EventDayService.selectForClosing(this.pending, 'all').length;
    }

    get todayCount(): number {
        return this.allCount - this.untilYesterdayCount;
    }

    /** Órdenes que entran con el alcance elegido (canceladas incluidas: también se marcan). */
    get selected(): any[] {
        return EventDayService.selectForClosing(this.pending, this.scope);
    }

    /** Días que se van a cerrar, con sus totales (solo órdenes vivas). */
    get selectedGroups(): DayGroup[] {
        const keys: { [day: string]: boolean } = {};
        this.selected.forEach(o => keys[EventDayService.orderDay(o)] = true);
        return this.groups.filter(g => keys[g.day]);
    }

    get selectedTotal(): number {
        return Math.round(this.selectedGroups.reduce((acc, g) => acc + g.total, 0) * 100) / 100;
    }

    get selectedCanceled(): number {
        return this.selected.filter(o => o.status === 'canceled' || o.status === 'cancelled').length;
    }

    get closeLabel(): string {
        const n = this.selected.length;
        const d = this.selectedGroups.length;
        if (n === 0) return 'Cerrar día';
        return `Cerrar ${n} ${n === 1 ? 'orden' : 'órdenes'} de ${d} ${d === 1 ? 'día' : 'días'}`;
    }

    lastRunDate(): Date | null {
        const raw: any = this.lastRun ? this.lastRun.closed_at : null;
        if (!raw) return null;
        return raw.toDate ? raw.toDate() : new Date(raw);
    }

    // ── Cerrar ─────────────────────────────────────────────────────────────

    async confirmClose() {
        if (this.step !== 'view' || this.selected.length === 0) return;
        const n = this.selected.length;
        const days = this.selectedGroups.map(g => g.label).join(', ');
        const confirm: any = await Swal.fire({
            title: `¿Cerrar ${n} ${n === 1 ? 'orden' : 'órdenes'}?`,
            html: `<p style="margin:0 0 8px;color:#6b7280;font-size:.92rem;line-height:1.45;">Se marcan como cerradas las órdenes de <strong>${days}</strong>`
                + (this.scope === 'all' && this.todayCount > 0 ? `, incluidas las <strong>${this.todayCount}</strong> de hoy` : '')
                + `. Dejan de verse en la caja como pendientes y el cliente las verá por día. Los inventarios y los arqueos no cambian.</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, cerrar el día',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#912721',
            cancelButtonColor: '#92949c',
        });
        if (!confirm || !confirm.value) return;

        this.step = 'working';
        this.error = '';
        this.progressText = `Marcando ${n} ${n === 1 ? 'orden' : 'órdenes'}…`;
        try {
            this.result = await this.service.closeDays(this.event_key, this.event_name, this.selected, this.scope);
            this.step = 'done';
        } catch (e) {
            console.error('No se pudo cerrar el día:', e);
            this.error = 'No se pudieron marcar todas las órdenes. Revisa la conexión y vuelve a intentar: las que ya se cerraron no se repiten.';
            this.step = 'view';
            await this.load();
        }
    }

    finish() {
        this.close({ closed: true, run: this.result });
    }
}
